import { supabase } from '../supabase/supabaseClient';

export interface Group {
  id: string;
  name: string;
  avatar_url: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
  invite_code?: string | null;
  member_count?: number;
  unread_count?: number;
  last_message?: {
    content: string;
    created_at: string;
  } | null;
}

export const getUserGroups = async (): Promise<{
  data: Group[] | null;
  error: string | null;
}> => {
  try {
    console.log(`👥 [GroupService] Fetching groups for current user`);
    const startTime = Date.now();

    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      console.error(`❌ [GroupService] Cannot fetch groups - user not authenticated`);
      return { data: null, error: 'User not authenticated' };
    }

    // Get groups where user is a member
    const { data: memberships, error: memberError } = await supabase
      .from('group_members')
      .select('group_id')
      .eq('user_id', user.id);

    if (memberError) {
      console.error(
        `❌ [GroupService] Error fetching memberships for user ${user.id}:`,
        memberError
      );
      return { data: null, error: memberError.message };
    }

    if (!memberships || memberships.length === 0) {
      console.log(`ℹ️ [GroupService] User is not a member of any groups`);
      return { data: [], error: null };
    }

    console.log(`✅ [GroupService] User is member of ${memberships.length} groups`);
    const groupIds = memberships.map(m => m.group_id);

    // Fetch groups
    const { data: groups, error: groupsError } = await supabase
      .from('groups')
      .select(
        `
        id,
        name,
        avatar_url,
        created_by,
        created_at,
        updated_at
      `
      )
      .in('id', groupIds)
      .order('updated_at', { ascending: false });

    if (groupsError) {
      console.error(`❌ [GroupService] Error fetching group details:`, groupsError);
      return { data: null, error: groupsError.message };
    }

    // Enrich with counts and last message
    const groupsWithCounts = await Promise.all(
      (groups || []).map(async group => {
        const { count } = await supabase
          .from('group_members')
          .select('*', { count: 'exact', head: true })
          .eq('group_id', group.id);

        const { data: memberData } = await supabase
          .from('group_members')
          .select('unread_count')
          .eq('group_id', group.id)
          .eq('user_id', user.id)
          .maybeSingle();

        const { data: lastMessage } = await supabase
          .from('messages')
          .select('content, created_at')
          .eq('group_id', group.id)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        return {
          ...group,
          member_count: count || 0,
          unread_count: memberData?.unread_count || 0,
          last_message: lastMessage || null,
        } as Group;
      })
    );

    const fetchTime = Date.now() - startTime;
    console.log(`✅ [GroupService] Fetched ${groupsWithCounts.length} groups in ${fetchTime}ms`);

    return { data: groupsWithCounts, error: null };
  } catch (error) {
    console.error(`❌ [GroupService] Unexpected error fetching groups:`, error);
    return { data: null, error: 'An unexpected error occurred' };
  }
};

export const createGroup = async (
  name: string
): Promise<{ data: Group | null; error: string | null }> => {
  try {
    console.log(`➕ [GroupService] Creating new group: "${name}"`);
    const startTime = Date.now();

    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      console.error(`❌ [GroupService] Cannot create group - user not authenticated`);
      return { data: null, error: 'User not authenticated' };
    }

    const { data: group, error: groupError } = await supabase
      .from('groups')
      .insert({ name, created_by: user.id })
      .select()
      .single();

    if (groupError) {
      console.error(`❌ [GroupService] Error creating group "${name}":`, groupError);
      return { data: null, error: groupError.message };
    }

    // Add creator as admin member
    const { error: memberError } = await supabase.from('group_members').insert({
      group_id: group.id,
      user_id: user.id,
      role: 'admin',
      can_send_messages: true,
    });

    if (memberError) {
      console.error(
        `❌ [GroupService] Error adding creator as member to group ${group.id}:`,
        memberError
      );
      return { data: null, error: memberError.message };
    }

    const createTime = Date.now() - startTime;
    console.log(
      `✅ [GroupService] Group "${name}" created successfully in ${createTime}ms (ID: ${group.id})`
    );

    return { data: { ...group, member_count: 1 }, error: null };
  } catch (error) {
    console.error(`❌ [GroupService] Unexpected error creating group "${name}":`, error);
    return { data: null, error: 'An unexpected error occurred' };
  }
};

export const getGroupByInviteCode = async (
  inviteCode: string
): Promise<{ data: Group | null; error: string | null }> => {
  try {
    console.log(`🔍 [GroupService] Looking up group with code: ${inviteCode}`);

    const { data: group, error: groupError } = await supabase
      .from('groups')
      .select('id, name, avatar_url, created_by, created_at, updated_at, invite_code')
      .eq('invite_code', inviteCode.toUpperCase())
      .single();

    if (groupError) {
      console.error(`❌ [GroupService] Error finding group with code ${inviteCode}:`, groupError);
      return { data: null, error: 'Invalid invite code' };
    }

    if (!group) {
      console.log(`ℹ️ [GroupService] No group found with code: ${inviteCode}`);
      return { data: null, error: 'Group not found' };
    }

    const { count } = await supabase
      .from('group_members')
      .select('*', { count: 'exact', head: true })
      .eq('group_id', group.id);

    return {
      data: { ...group, member_count: count || 0 },
      error: null,
    };
  } catch (error) {
    console.error(`❌ [GroupService] Unexpected error looking up invite code:`, error);
    return { data: null, error: 'An unexpected error occurred' };
  }
};

export const joinGroupByInviteCode = async (
  inviteCode: string
): Promise<{ success: boolean; groupId?: string; error?: string }> => {
  try {
    console.log(`🚪 [GroupService] Attempting to join group with code: ${inviteCode}`);
    const startTime = Date.now();

    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      console.error(`❌ [GroupService] Cannot join group - user not authenticated`);
      return { success: false, error: 'User not authenticated' };
    }

    const { data: group, error: lookupError } = await getGroupByInviteCode(inviteCode);
    if (lookupError || !group) {
      return { success: false, error: lookupError || 'Group not found' };
    }

    // Check if already a member
    const { data: existing } = await supabase
      .from('group_members')
      .select('id')
      .eq('group_id', group.id)
      .eq('user_id', user.id)
      .maybeSingle();

    if (existing) {
      console.log(`ℹ️ [GroupService] User already member of group ${group.id}`);
      return { success: true, groupId: group.id };
    }

    // Add user as member
    const { error: memberError } = await supabase.from('group_members').insert({
      group_id: group.id,
      user_id: user.id,
      role: 'member',
      can_send_messages: true,
    });

    if (memberError) {
      console.error(`❌ [GroupService] Error adding user to group ${group.id}:`, memberError);
      return { success: false, error: 'Failed to join group' };
    }

    const joinTime = Date.now() - startTime;
    console.log(`✅ [GroupService] User joined group "${group.name}" in ${joinTime}ms`);
    return { success: true, groupId: group.id };
  } catch (error) {
    console.error(`❌ [GroupService] Unexpected error joining group:`, error);
    return { success: false, error: 'An unexpected error occurred' };
  }
};

export const getGroupInviteCode = async (
  groupId: string
): Promise<{ inviteCode: string | null; error: string | null }> => {
  try {
    console.log(`🔑 [GroupService] Fetching invite code for group: ${groupId}`);

    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      return { inviteCode: null, error: 'User not authenticated' };
    }

    // Check if user is member of the group
    const { data: membership } = await supabase
      .from('group_members')
      .select('role')
      .eq('group_id', groupId)
      .eq('user_id', user.id)
      .maybeSingle();

    if (!membership) {
      return { inviteCode: null, error: 'You are not a member of this group' };
    }

    const { data: group, error: groupError } = await supabase
      .from('groups')
      .select('invite_code')
      .eq('id', groupId)
      .single();

    if (groupError || !group) {
      console.error(`❌ [GroupService] Error fetching invite code:`, groupError);
      return { inviteCode: null, error: 'Failed to get invite code' };
    }

    console.log(`✅ [GroupService] Retrieved invite code for group ${groupId}`);
    return { inviteCode: group.invite_code, error: null };
  } catch (error) {
    console.error(`❌ [GroupService] Unexpected error getting invite code:`, error);
    return { inviteCode: null, error: 'An unexpected error occurred' };
  }
};

export interface GroupMember {
  id: string;
  group_id: string;
  user_id: string;
  role: 'admin' | 'member';
  can_send_messages: boolean;
  joined_at: string;
  user: {
    id: string;
    name: string;
    email: string;
    avatar_url: string | null;
  };
}

export const getGroupMembers = async (
  groupId: string
): Promise<{ data: GroupMember[] | null; error: string | null }> => {
  try {
    console.log(`👥 [GroupService] Fetching members for group: ${groupId}`);

    const { data: members, error: membersError } = await supabase
      .from('group_members')
      .select(
        `
        id,
        group_id,
        user_id,
        role,
        can_send_messages,
        joined_at,
        profiles:user_id (
          id,
          name,
          email,
          avatar_url
        )
      `
      )
      .eq('group_id', groupId)
      .order('joined_at', { ascending: true });

    if (membersError) {
      console.error(`❌ [GroupService] Error fetching members:`, membersError);
      return { data: null, error: membersError.message };
    }

    const formattedMembers: GroupMember[] = (members || []).map((member: any) => ({
      id: member.id,
      group_id: member.group_id,
      user_id: member.user_id,
      role: member.role,
      can_send_messages: member.can_send_messages,
      joined_at: member.joined_at,
      user: {
        id: member.profiles.id,
        name: member.profiles.name,
        email: member.profiles.email,
        avatar_url: member.profiles.avatar_url,
      },
    }));

    console.log(
      `✅ [GroupService] Fetched ${formattedMembers.length} members for group ${groupId}`
    );
    return { data: formattedMembers, error: null };
  } catch (error) {
    console.error(`❌ [GroupService] Unexpected error fetching members:`, error);
    return { data: null, error: 'An unexpected error occurred' };
  }
};
